// StudentFullProfile.jsx - Professionally Enhanced Unified Layout for Profile Sections
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
    Box, Card, Typography, Avatar, Grid, Chip, Divider,
    Tooltip, IconButton, CircularProgress, CardContent,
    CardHeader, AppBar, Toolbar, Switch
} from "@mui/material";
import { motion } from "framer-motion";
import TelegramIcon from "@mui/icons-material/Telegram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import ApartmentIcon from "@mui/icons-material/Apartment";
import EventIcon from "@mui/icons-material/Event";
import { API_ENDPOINTS, getUploadUrl } from "config/api";

const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
        case "facebook": return <FacebookIcon sx={{ fontSize: 28, color: "#1877f2" }} />;
        case "linkedin": return <LinkedInIcon sx={{ fontSize: 28, color: "#0a66c2" }} />;
        case "telegram": return <TelegramIcon sx={{ fontSize: 28, color: "#0088cc" }} />;
        case "instagram": return <InstagramIcon sx={{ fontSize: 28, color: "#e1306c" }} />;
        default: return null;
    }
};

const ProfileSectionCard = ({ title, icon, visible, onToggle, items = [], chipColor = "info" }) => (
    <Card sx={{ boxShadow: 2, borderRadius: 3, mb: 2, p: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
            {icon}
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 15, flexGrow: 1, color: "#1976d2", ml: 1 }}>{title}</Typography>
            <Switch checked={visible} onChange={onToggle} color="info" size="small" />
        </Box>
        {visible && (
            <Box display="flex" gap={1} flexWrap="wrap">
                {items.map((item, idx) => (
                    <Chip key={idx} label={item} color={chipColor} />
                ))}
            </Box>
        )}
    </Card>
);

ProfileSectionCard.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.element.isRequired,
    visible: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    items: PropTypes.arrayOf(PropTypes.string).isRequired,
    chipColor: PropTypes.string
};

function StudentFullProfile({ studentId, token }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [privacy, setPrivacy] = useState({
        isChildrenVisible: true,
        isOccupationVisible: true,
        isChapterVisible: true,
        isEventsVisible: true,
    });

    useEffect(() => {
        if (!studentId || !token) return;
        axios.get(API_ENDPOINTS.PROFILE.GET_FULL_PROFILE(studentId), {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => setProfile(res.data))
            .catch(() => setProfile(null))
            .finally(() => setLoading(false));
    }, [studentId, token]);

    if (loading) return <Box p={2}><CircularProgress /></Box>;
    if (!profile) return <Typography>No profile data available.</Typography>;

    const { coreProfile = {}, chapterMemberships = [], chapterEvents = [], children = [], occupations = [], socialLinks = [], expertises = [] } = profile;

    return (
        <Box>
            <AppBar position="sticky" elevation={4} sx={{ mb: 2, background: "linear-gradient(to right, #6a11cb, #2575fc)" }}>
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1, color: "#fff" }}>
                        🎓 Alumni Full Profile
                    </Typography>
                </Toolbar>
            </AppBar>

            <Box px={3} pb={4}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <Card sx={{ mb: 3, borderRadius: 4, boxShadow: 6, border: "1px solid #e0e0e0" }}>
                        <CardHeader
                            avatar={<Avatar src={coreProfile.profileImage ? getUploadUrl(coreProfile.profileImage) : undefined} alt={coreProfile.fullName} sx={{ width: 72, height: 72, fontSize: 28, bgcolor: "primary.main", boxShadow: 3, border: "2px solid #fff" }}>{!coreProfile.profileImage && coreProfile.fullName?.[0]}</Avatar>}
                            title={<Typography variant="h6" fontWeight={600}>{coreProfile.fullName}</Typography>}
                            subheader={<Typography variant="body2" color="text.secondary">{coreProfile.email1}</Typography>}
                        />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6} lg={3}>
                                    <Typography variant="body2"><strong>Mobile:</strong> {coreProfile.selfMobile}</Typography>
                                    <Typography variant="body2"><strong>DOB:</strong> {coreProfile.dob}</Typography>
                                    <Typography variant="body2"><strong>Batch:</strong> {coreProfile.batchYear}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6} lg={3}>
                                    <Typography variant="body2"><strong>Course:</strong> {coreProfile.courseName}</Typography>
                                    <Typography variant="body2"><strong>Degree:</strong> {coreProfile.degree}</Typography>
                                    <Typography variant="body2"><strong>First Graduate:</strong> {coreProfile.isFirstGraduate ? "Yes" : "No"}</Typography>
                                </Grid>
                                <Grid item xs={12} md={12} lg={6}>
                                    {coreProfile.profileDesc && <Box><Divider sx={{ mb: 1 }} /><Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>{coreProfile.profileDesc}</Typography></Box>}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <ProfileSectionCard
                        title="Children Details"
                        icon={<ChildCareIcon color="info" sx={{ fontSize: 28 }} />}
                        visible={privacy.isChildrenVisible}
                        onToggle={e => setPrivacy({ ...privacy, isChildrenVisible: e.target.checked })}
                        items={children.map(c => `${c.childName} (${c.gender}, ${c.star}, ${c.rasi})`)}
                    />

                    <ProfileSectionCard
                        title="Occupation History"
                        icon={<WorkHistoryIcon color="info" sx={{ fontSize: 28 }} />}
                        visible={privacy.isOccupationVisible}
                        onToggle={e => setPrivacy({ ...privacy, isOccupationVisible: e.target.checked })}
                        items={occupations.map(o => `${o.designation} at ${o.company} - ${o.district}, ${o.country}`)}
                    />

                    <ProfileSectionCard
                        title="Chapter Memberships"
                        icon={<ApartmentIcon color="info" sx={{ fontSize: 28 }} />}
                        visible={privacy.isChapterVisible}
                        onToggle={e => setPrivacy({ ...privacy, isChapterVisible: e.target.checked })}
                        items={chapterMemberships.map(c => c.chapterByMembership)}
                    />

                    <ProfileSectionCard
                        title="Chapter Events"
                        icon={<EventIcon color="info" sx={{ fontSize: 28 }} />}
                        visible={privacy.isEventsVisible}
                        onToggle={e => setPrivacy({ ...privacy, isEventsVisible: e.target.checked })}
                        items={chapterEvents.map(e => e.chapterByEvent)}
                        chipColor="success"
                    />

                    {socialLinks.length > 0 && (
                        <Card sx={{ mt: 3, p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>Social Media</Typography>
                            <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                                {socialLinks.map((link, idx) => (
                                    <Tooltip title={link.platform} key={idx}>
                                        <IconButton href={link.profileUrl} target="_blank" rel="noopener noreferrer" sx={{ border: '1px solid #ccc', borderRadius: 2, '&:hover': { backgroundColor: '#f0f0f0', transform: 'scale(1.1)', transition: '0.3s ease' } }}>
                                            {getSocialIcon(link.platform)}
                                        </IconButton>
                                    </Tooltip>
                                ))}
                            </Box>
                        </Card>
                    )}

                    {expertises.length > 0 && (
                        <Card sx={{ mt: 3, p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600}>Expertise</Typography>
                            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                                {expertises.map((exp, idx) => (
                                    <Chip key={idx} label={exp.expertiseName} variant="outlined" color="primary" sx={{ fontWeight: 500, borderRadius: 1, px: 1.5, fontSize: 13 }} />
                                ))}
                            </Box>
                        </Card>
                    )}
                </motion.div>
            </Box>
        </Box>
    );
}

StudentFullProfile.propTypes = {
    studentId: PropTypes.number.isRequired,
    token: PropTypes.string.isRequired,
};

export default StudentFullProfile;
